/**
 * CeremonyError — the canonical failure surface for every
 * passkey ceremony in PureID.
 *
 * Renders the output of `classifyCeremonyFailure` as a small
 * card with:
 *
 *   - title + body sourced from CEREMONY_ERROR_COPY (the closed
 *     code → copy table in @pureid/auth-core/copy)
 *   - a primary action button matching the structured remedy
 *     (the consumer wires the onClick — usually "retry the
 *     same ceremony", "open the transport picker", or
 *     "navigate to support contact")
 *   - an optional secondary "try a different method" link
 *     (only when the primary remedy is itself "retry"; if the
 *     primary is already "try a different method" we don't
 *     duplicate)
 *   - an optional "Learn more" external link sourced from the
 *     copy table (e.g. the Microsoft Hello setup page)
 *   - a collapsible "Show diagnostic details" disclosure that
 *     reveals a copy-to-clipboard support bundle. The bundle
 *     is the concatenation of:
 *
 *       Code: <code>
 *       Browser: <browser> <version> on <os>
 *       Ceremony ID: <correlationId>
 *       DOMException: <name>
 *
 *     plus optional capability flags. Operators paste this
 *     into search and find the matching telemetry events.
 *
 * # Why one component
 *
 * Every onboarding surface (admin login, admin enrollment,
 * portal login, step-up dialog) used to render its own
 * ad-hoc error block — leading to drift in tone, missing
 * diagnostics, and inconsistent remedy CTAs. Funneling all
 * four through this single primitive guarantees the user
 * sees the same words and the same support bundle regardless
 * of where the failure happened.
 *
 * # Accessibility
 *
 * The error is rendered inside a `role="alert"` region so
 * screen readers announce it on mount. Diagnostic disclosure
 * uses the native `<details>` element (always-keyboard
 * focusable, reads correctly on every screen reader without
 * a polyfill).
 */

import * as React from "react";
import { AlertTriangle, ExternalLink, RotateCw, Settings } from "lucide-react";

import type { CapabilitySnapshot, UaHint } from "@pureid/auth-core";
import {
  copyForCode,
  invalidateProbeCache,
  openSetupLink,
  platformAuthenticatorSetupLink,
  type CeremonyClassification,
  type CeremonyRemedy,
} from "@pureid/auth-core";

import { cn } from "../../lib/utils";
import { Button } from "../button";
import { CopyButton } from "../copy-button";
import { ErrorIllustration, type ErrorIllustrationCode } from "./error-illustration";

// Visual onboarding (contract §4.4): map a CeremonyError
// classification code onto the ErrorIllustration variant
// designed for it. Returns null when no dedicated
// illustration applies — those errors render text-only.
//
// Coverage of the 15-code classifier (classify-ceremony.ts
// ClassifiedCeremonyErrorCode):
//
//   P0 (refined 2026-05-11):
//   - user_cancelled / cancelled       → user-cancelled
//   - platform_unavailable             → platform-authenticator-missing
//   - hybrid_unreachable               → hybrid-bluetooth-unavailable
//   - no_authenticator_available       → no-credentials
//   - credentials_on_other_device      → no-credentials (shared)
//
//   P1 (first-cut, refinement pending):
//   - timeout                          → took-too-long
//   - already_registered               → already-registered
//   - wrong_authenticator              → wrong-key
//   - not_supported / security_error   → browser-blocked
//   - abort / begin_failed / verify_failed
//                                      → interrupted
//   - ceremony_failed                  → interrupted (catch-all)
//
//   Text-only, deliberately:
//   - passkey_limit_reached            → none. Every illustration
//     above depicts something that went WRONG; a tenant cap
//     refusing a registration is the policy working, and the
//     card's own words carry it.
//   - hint_not_offered                 → none, for the same reason.
function illustrationFor(code: string): ErrorIllustrationCode | null {
  switch (code) {
    case "user_cancelled":
    case "cancelled":
      return "user-cancelled";
    case "platform_unavailable":
      return "platform-authenticator-missing";
    case "hybrid_unreachable":
      return "hybrid-bluetooth-unavailable";
    case "no_authenticator_available":
    case "credentials_on_other_device":
      return "no-credentials";
    case "timeout":
      return "took-too-long";
    case "already_registered":
      return "already-registered";
    case "wrong_authenticator":
      return "wrong-key";
    case "not_supported":
    case "security_error":
      return "browser-blocked";
    case "abort":
    case "begin_failed":
    case "verify_failed":
    case "ceremony_failed":
      return "interrupted";
    default:
      return null;
  }
}

/**
 * UX-F: a tenant-configured support channel the surface
 * threads through to error / help renders. When present, the
 * `<CeremonyError>` disclosure renders a "Contact your
 * administrator" CTA (mailto: when email is set) so a
 * stranded user always has a path back to their employer's
 * IT desk without leaving the platform.
 */
export interface SupportContact {
  email?: string;
}

export interface CeremonyErrorProps {
  /** The classifier output. Required. */
  classification: CeremonyClassification;
  /**
   * The browser-side ceremony correlation UUID. Operators paste
   * this into log search to retrieve the full timeline. When
   * present we render it inside the diagnostic disclosure AND
   * in the support bundle.
   */
  ceremonyId?: string;
  /**
   * Capability snapshot at the moment the failure happened.
   * Optional — omit when the consumer doesn't have it yet.
   * When present we surface only the booleans we know about so
   * the disclosure stays scannable.
   */
  capability?: CapabilitySnapshot;
  /**
   * Browser/OS hint. Same role as `capability` — purely
   * diagnostic, included in the bundle.
   */
  ua?: UaHint;
  /**
   * Primary CTA handler. Required. The label is sourced from
   * the copy table; this is just the click handler.
   */
  onPrimary: () => void;
  /**
   * Secondary CTA handler. When present, renders below the
   * primary as a small link-styled button. Typical use: "Try
   * a different method" while the primary is "Try again".
   */
  onSecondary?: () => void;
  /**
   * Override the secondary CTA label. When unset, defaults to
   * "Try a different method" — appropriate when the primary is
   * "retry" and the user might want to switch transports
   * instead.
   */
  secondaryLabel?: string;
  /**
   * Whether to show the secondary link. Even with `onSecondary`
   * set, the consumer can hide the secondary in places where
   * there's nowhere else to go (e.g. a dialog already showing
   * the picker).
   */
  showSecondary?: boolean;
  /**
   * UX-F: when present, the disclosure renders a "Contact
   * your administrator" CTA pointing at the configured
   * channel. Accepts the tenant projection from
   * /api/loginflow/v1/config or the wizard's GetStatus
   * response. Empty fields collapse to a non-clickable
   * "contact your administrator" hint.
   */
  supportContact?: SupportContact;
  /**
   * Re-probe handler. When set, the surface renders an
   * "I've set it up — try again" button on capability-related
   * codes (`platform_unavailable`, `hybrid_unreachable`) that
   * invalidates the device-capability probe cache and invokes
   * the consumer's callback.
   *
   * Typical wiring: the consumer passes a callback that calls
   * `probeCapabilities()` again and updates state. Without this
   * prop the button is hidden — older surfaces that haven't
   * adopted re-probing keep their existing UX.
   *
   * The button appears ONLY for codes where the user can
   * actually fix the device-level prerequisite (Windows Hello
   * setup, Bluetooth toggle). For an `already_registered` or
   * `not_supported` code, re-probing changes nothing, so the
   * button stays hidden even when this callback is supplied.
   */
  onSetupComplete?: () => void;
  className?: string;
}

export function CeremonyError({
  classification,
  ceremonyId,
  capability,
  ua,
  onPrimary,
  onSecondary,
  secondaryLabel,
  showSecondary = true,
  supportContact,
  onSetupComplete,
  className,
}: CeremonyErrorProps) {
  const copy = copyForCode(classification.code);
  const supportBundle = buildSupportBundle({
    classification,
    ceremonyId,
    capability,
    ua,
  });

  // Resolve the setup deep link only on `platform_unavailable`.
  // The other capability-fail codes have different remedies:
  //   - hybrid_unreachable → user toggles Bluetooth (no
  //     OS-scheme deep link is reliable for that — Bluetooth
  //     panel paths differ across Win10/Win11/macOS versions)
  //   - no_authenticator_available → ambiguous; could be either,
  //     so the surface stays neutral
  // Hiding the button on those codes keeps the CTA semantics
  // honest — only show "Set up Windows Hello" when that is the
  // actually-correct next action.
  const setupLink =
    classification.code === "platform_unavailable" && ua
      ? platformAuthenticatorSetupLink(ua)
      : null;

  // Re-probe only for codes where the user can fix the
  // prerequisite. See onSetupComplete prop docs for the rationale.
  const canReprobe =
    onSetupComplete &&
    (classification.code === "platform_unavailable" ||
      classification.code === "hybrid_unreachable");

  function handleSetupClick() {
    if (!setupLink) return;
    openSetupLink(setupLink);
  }

  function handleSetupComplete() {
    invalidateProbeCache();
    onSetupComplete?.();
  }

  // Visual onboarding (contract §4.4): map the classification
  // code to one of the five illustration variants. When the
  // classification doesn't have a dedicated illustration, the
  // error card renders text-only as before.
  const illustrationCode = illustrationFor(classification.code);

  return (
    <div
      role="alert"
      className={cn(
        "rounded-xl border border-danger/25 bg-danger/5 p-4 space-y-3",
        className,
      )}
    >
      {illustrationCode && (
        <div className="flex justify-center pb-2">
          <ErrorIllustration code={illustrationCode} width={200} />
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-8 w-8 rounded-lg bg-danger/15 text-danger flex items-center justify-center shrink-0">
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground">{copy.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{copy.body}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {/* When a setup deep link applies, it takes the primary
            slot — the genuinely correct next action is to enable
            the platform authenticator, not to "try a different
            method" that's likely also unavailable. The original
            primary CTA from the copy table demotes to a small
            "Try a different method" link below. */}
        {setupLink ? (
          <Button type="button" className="w-full" onClick={handleSetupClick}>
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            {setupLink.label}
          </Button>
        ) : (
          <Button type="button" className="w-full" onClick={onPrimary}>
            {copy.primaryCta}
          </Button>
        )}

        {/* "I've set it up — try again": only visible for codes
            where re-probing can change the answer. Calls
            invalidateProbeCache() before the consumer callback
            so the next probeCapabilities() reads fresh state
            instead of the memoised "no" from the failed attempt. */}
        {canReprobe && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleSetupComplete}
          >
            <RotateCw className="h-3.5 w-3.5 mr-1.5" />
            I&rsquo;ve set it up — try again
          </Button>
        )}

        {/* When the deep-link primary is in play, the original
            copy.primaryCta becomes the "alternative" link — the
            user who doesn't want to set up Windows Hello right
            now can still pick a different method. */}
        {setupLink && (
          <button
            type="button"
            onClick={onPrimary}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          >
            {copy.primaryCta}
          </button>
        )}

        {showSecondary && onSecondary && (
          <button
            type="button"
            onClick={onSecondary}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          >
            {secondaryLabel ?? "Try a different method"}
          </button>
        )}

        {copy.learnMore && (
          <a
            href={copy.learnMore.href}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {copy.learnMore.label}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}

        {supportContact?.email ? (
          // UX-F: tenant-configured support inbox. mailto: opens
          // the user's mail client with the support bundle in
          // the body so the operator gets a triage-ready email
          // without the user having to copy + paste the
          // disclosure. Subject is brand-stable; body carries
          // the bundle the diagnostic-disclosure shows below
          // for convenience.
          <a
            href={buildSupportMailto(supportContact.email, classification, ceremonyId, ua, capability)}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Contact your administrator ({supportContact.email})
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>

      <details className="group">
        <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground list-none flex items-center gap-1.5 [&::-webkit-details-marker]:hidden">
          <span className="transition-transform group-open:rotate-90">▸</span>
          Show diagnostic details
        </summary>
        <div className="mt-2 rounded-lg border border-border/50 bg-muted/30 p-3 space-y-2">
          <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap break-all leading-relaxed">
            {supportBundle}
          </pre>
          <div className="flex justify-end">
            <CopyButton value={supportBundle} size="sm" />
          </div>
          <p className="text-xs text-muted-foreground/70">
            Share this with your administrator if the problem persists. It
            never includes your password or passkey credentials.
          </p>
        </div>
      </details>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

interface BuildSupportBundleArgs {
  classification: CeremonyClassification;
  ceremonyId?: string;
  capability?: CapabilitySnapshot;
  ua?: UaHint;
}

/**
 * Build the operator-friendly support bundle string.
 *
 * The format is line-oriented `Key: value` so operators can
 * paste straight into log search. Lines are stable across
 * languages — even when copy is translated, this bundle stays
 * in English so the operator search syntax doesn't depend on
 * the user's locale.
 *
 * NEVER includes anything PII-shaped: no email, no token, no
 * credential bytes. The classification.domException.message is
 * captured verbatim because the upstream redactor (in the
 * telemetry reporter) catches anything that slipped past the
 * client-side scrubber, but for the UI render we additionally
 * truncate to 256 chars to keep the disclosure compact.
 */
export function buildSupportBundle({
  classification,
  ceremonyId,
  capability,
  ua,
}: BuildSupportBundleArgs): string {
  const lines: string[] = [];
  lines.push(`Code: ${classification.code}`);
  if (ua) {
    lines.push(`Browser: ${ua.browser} ${ua.version} on ${ua.os}`);
  }
  if (ceremonyId) {
    lines.push(`Ceremony ID: ${ceremonyId}`);
  }
  if (classification.domException) {
    lines.push(`DOMException: ${classification.domException.name}`);
    if (classification.domException.message) {
      const msg = classification.domException.message.slice(0, 256);
      lines.push(`Message: ${msg}`);
    }
  }
  if (capability) {
    lines.push(`Capability:`);
    lines.push(`  WebAuthn: ${capability.webauthn}`);
    lines.push(`  Secure context: ${capability.secureContext}`);
    lines.push(`  Platform authenticator: ${nullableLabel(capability.platformAuthenticator)}`);
    lines.push(`  Conditional mediation: ${nullableLabel(capability.conditionalMediation)}`);
  }
  return lines.join("\n");
}

function nullableLabel(v: boolean | null): string {
  if (v === true) return "yes";
  if (v === false) return "no";
  return "unknown";
}

/**
 * Build a `mailto:` URL pre-filled with the support bundle so
 * the user's mail client opens with a triage-ready email — no
 * copy/paste step. Subject is stable; body is the same bundle
 * the diagnostic disclosure shows. URL-encoded per RFC 6068
 * (`?subject=...&body=...`).
 *
 * The function NEVER includes the user's email address — only
 * the codes and capability flags. Privacy guarantee matches the
 * disclosure: nothing in the mailto carries identifiers.
 */
export function buildSupportMailto(
  email: string,
  classification: CeremonyClassification,
  ceremonyId?: string,
  ua?: UaHint,
  capability?: CapabilitySnapshot,
): string {
  const subject = `Sign-in trouble: ${classification.code}`;
  const intro =
    "Hi — I'm having trouble signing in. The platform suggested I share these details:\n\n";
  const bundle = buildSupportBundle({
    classification,
    ceremonyId,
    ...(capability ? { capability } : {}),
    ...(ua ? { ua } : {}),
  });
  const closing =
    "\n\nThanks for your help.";
  const body = intro + bundle + closing;
  // encodeURIComponent handles every char that mailto: cares
  // about (newline → %0A, & → %26, etc.). Not all email
  // clients honour the body parameter — Outlook desktop
  // historically truncated long values — but the diagnostic
  // disclosure stays available as a fallback so the user can
  // still copy + paste manually.
  return (
    "mailto:" + encodeURIComponent(email) +
    "?subject=" + encodeURIComponent(subject) +
    "&body=" + encodeURIComponent(body)
  );
}

/**
 * Convenience: pick a sensible default secondary CTA label
 * based on the structured remedy. The primary CTA already
 * reflects the recommended path; the secondary is the
 * "try something else" affordance, which is mostly always
 * "Try a different method" but reads better as "Choose a
 * different method" when the primary is itself a try-again.
 */
export function defaultSecondaryLabel(remedy: CeremonyRemedy): string {
  switch (remedy.kind) {
    case "retry":
    case "enable_platform_auth":
    case "enable_bluetooth":
      return "Choose a different method";
    case "try_other_transport":
    case "register_first":
    case "contact_support":
      return "Try again";
  }
}
